import React from 'react';
import { theme } from 'antd';
import wolfLogo from './wolf.png';

/**
 * AppLogo — GameBooking brand mark + wordmark, with the golden wolf on the right.
 *
 * Props:
 *   size      — icon diameter in px (default 48)
 *   showText  — render "GameBooking" wordmark (default true)
 *   textSize  — wordmark font size in px (default 22)
 *   color     — override primary accent colour
 *   style     — extra container styles
 */
export default function AppLogo({
  size = 48,
  showText = true,
  textSize = 22,
  color,
  style,
}) {
  const { token } = theme.useToken();
  const accent = color || token.colorPrimary;
  const gradId = React.useId().replace(/:/g, '');

  // ── Pitch geometry (ViewBox 0 0 100 100) ──────────────────────────────
  // Badge clip: shield/crest path
  // Pitch rect: px=15 py=24 pw=70 ph=50  →  right edge at 85, bottom at 74
  // Proportions mirror a real 105×68m pitch scaled to 70×50px
  const px = 15; const py = 24; const pw = 70; const ph = 50;
  const pr = px + pw; // 85 — right edge
  const pb = py + ph; // 74 — bottom edge
  const cx = px + pw / 2; // 50 — horizontal center
  const cy = py + ph / 2; // 49 — vertical center

  // Penalty area: 16.5m deep × 40.32m wide  →  11 × 30 px
  const paD = Math.round((pw * 16.5) / 105); // depth  ≈ 11
  const paH = Math.round((ph * 40.32) / 68); // height ≈ 29
  const paY = cy - paH / 2;

  // Goal area: 5.5m deep × 18.32m wide  →  4 × 13 px
  const gaD = Math.round((pw * 5.5) / 105); // depth  ≈ 4
  const gaH = Math.round((ph * 18.32) / 68); // height ≈ 13
  const gaY = cy - gaH / 2;

  // Corner arc radius: 1m  →  ~0.67px  (use 3 for visibility)
  const car = 3;

  const sw = Math.max(1, size / 52); // stroke width scales with icon size

  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10, userSelect: 'none', ...style }}>
      {/* ── Symbol ──────────────────────────────────────────────────── */}
      <svg
        width={size}
        height={size}
        viewBox="0 0 100 100"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-label="GameBooking logo">

        <defs>
          <linearGradient id={`lg-${gradId}`} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={accent} />
            <stop offset="100%" stopColor={accent} stopOpacity="0.75" />
          </linearGradient>

          <filter id={`sh-${gradId}`} x="-10%" y="-10%" width="120%" height="120%">
            <feDropShadow dx="0" dy="2" stdDeviation="3" floodOpacity="0.25" />
          </filter>

          {/* Badge / crest clip path */}
          <clipPath id={`cp-${gradId}`}>
            <path d="M 50 4 C 50 4 92 18 92 18 L 92 56 C 92 78 72 93 50 97 C 28 93 8 78 8 56 L 8 18 Z" />
          </clipPath>
        </defs>

        {/* Badge background */}
        <path
          d="M 50 4 C 50 4 92 18 92 18 L 92 56 C 92 78 72 93 50 97 C 28 93 8 78 8 56 L 8 18 Z"
          fill={`url(#lg-${gradId})`}
          filter={`url(#sh-${gradId})`}
        />

        {/* ── Pitch lines (all clipped to badge) ──────────────────── */}
        <g
          clipPath={`url(#cp-${gradId})`}
          stroke="white"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none">

          {/* Outer boundary */}
          <rect x={px} y={py} width={pw} height={ph} strokeWidth={sw * 2} />

          {/* Halfway / center line */}
          <line x1={cx} y1={py} x2={cx} y2={pb} strokeWidth={sw * 1.5} />

          {/* Center circle */}
          <circle cx={cx} cy={cy} r={10} strokeWidth={sw * 1.5} />

          {/* Center spot */}
          <circle cx={cx} cy={cy} r={sw * 1.2} fill="white" strokeWidth={0} />

          {/* Left penalty area */}
          <rect x={px} y={paY} width={paD} height={paH} strokeWidth={sw * 1.5} />

          {/* Left goal area */}
          <rect x={px} y={gaY} width={gaD} height={gaH} strokeWidth={sw * 1.5} />

          {/* Right penalty area */}
          <rect x={pr - paD} y={paY} width={paD} height={paH} strokeWidth={sw * 1.5} />

          {/* Right goal area */}
          <rect x={pr - gaD} y={gaY} width={gaD} height={gaH} strokeWidth={sw * 1.5} />

          {/* Corner arcs — small quarter circles at each corner */}
          {/* Top-left */}
          <path d={`M ${px + car},${py} A ${car},${car} 0 0 0 ${px},${py + car}`} strokeWidth={sw * 1.5} />
          {/* Top-right */}
          <path d={`M ${pr - car},${py} A ${car},${car} 0 0 1 ${pr},${py + car}`} strokeWidth={sw * 1.5} />
          {/* Bottom-left */}
          <path d={`M ${px},${pb - car} A ${car},${car} 0 0 0 ${px + car},${pb}`} strokeWidth={sw * 1.5} />
          {/* Bottom-right */}
          <path d={`M ${pr},${pb - car} A ${car},${car} 0 0 1 ${pr - car},${pb}`} strokeWidth={sw * 1.5} />
        </g>
      </svg>

      {/* ── Wordmark ──────────────────────────────────────────────── */}
      {showText && (
        <span style={{ fontSize: textSize, lineHeight: 1, letterSpacing: '-0.5px', fontFamily: 'inherit' }}>
          <span style={{ fontWeight: 300, color: token.colorText }}>Game</span>
          <span style={{ fontWeight: 800, color: accent }}>Booking</span>
        </span>
      )}

      {/* ── Wolf mark (right side) ────────────────────────────────── */}
      <img
        src={wolfLogo}
        alt="Wolf"
        style={{ height: size, width: 'auto', maxWidth: size, objectFit: 'contain', display: 'block', flexShrink: 0 }}
      />
    </div>
  );
}
